import { useState, useCallback, useRef } from 'react';
import { useAppConfig } from 'app/cross-cutting-concerns/configuration/hooks/useAppConfig';

interface PlaceSuggestion {
  description: string;
  placeId: string;
}

interface AddressComponents {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface UseGooglePlacesAutocompleteReturn {
  suggestions: PlaceSuggestion[];
  isLoading: boolean;
  fetchSuggestions: (input: string) => void;
  getPlaceDetails: (placeId: string) => Promise<AddressComponents | null>;
  clearSuggestions: () => void;
}

export const useGooglePlacesAutocomplete = (): UseGooglePlacesAutocompleteReturn => {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const {
    googleMaps: { apiKey },
  } = useAppConfig();

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input || input.length < 3) {
        setSuggestions([]);
        return;
      }

      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setIsLoading(true);

      try {
        const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': apiKey,
          },
          body: JSON.stringify({
            input,
            includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
            languageCode: 'en',
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Places API Error:', response.status, errorData);
          throw new Error(`Failed to fetch suggestions: ${response.status} ${response.statusText}`);
        }

        const data: { suggestions: google.maps.places.AutocompleteSuggestion[] } = await response.json();

        if (data.suggestions) {
          setSuggestions(
            data.suggestions.map((suggestion: google.maps.places.AutocompleteSuggestion) => ({
              description: suggestion.placePrediction?.text?.text || '',
              placeId: suggestion.placePrediction?.placeId || '',
            }))
          );
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error fetching address suggestions:', error);
          setSuggestions([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [apiKey]
  );

  const parseAddressComponents = (place: google.maps.places.Place): AddressComponents => {
    const components = place.addressComponents || [];
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zipCode = '';

    components.forEach((component: google.maps.places.AddressComponent) => {
      const types = component.types;

      types.forEach((type: string) => {
        if (type === 'street_number') {
          streetNumber = component.longText || '';
        } else if (type === 'route') {
          route = component.longText || '';
        } else if (type === 'locality') {
          city = component.longText || '';
        } else if (type === 'administrative_area_level_1') {
          state = component.shortText || '';
        } else if (type === 'postal_code') {
          zipCode = component.longText || '';
        }
      });
    });

    const address = streetNumber && route ? `${streetNumber} ${route}` : place.formattedAddress || '';

    return {
      address,
      city,
      state,
      zipCode,
    };
  };

  const getPlaceDetails = useCallback(
    async (placeId: string): Promise<AddressComponents | null> => {
      try {
        const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': 'addressComponents,formattedAddress',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch place details');
        }

        const data = await response.json();
        const addressComponents = parseAddressComponents(data);
        return addressComponents;
      } catch (error) {
        console.error('Error fetching place details:', error);
        return null;
      }
    },
    [apiKey]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    isLoading,
    fetchSuggestions,
    getPlaceDetails,
    clearSuggestions,
  };
};
